import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, SPORTS, isValidEmail } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Trophy, Calendar, MapPin, UserPlus, CheckCircle } from 'lucide-react';
import { cn } from '../lib/utils';

const PublicRegistration = () => {
  const { tournamentId } = useParams();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    gender: '',
    age: '',
    skill_level: 'intermediate',
    sports: [],
    rating: '',
    team_name: '',
    club: '',
    division_id: ''
  });

  useEffect(() => {
    if (!tournamentId) return;
    const fetchInfo = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/tournaments/${tournamentId}/registration`);
        setInfo(res.data);
      } catch (err) {
        if (err.response?.status === 404) setInfo(null);
        else setInfo({ error: err.response?.data?.detail || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [tournamentId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!info?.registration_open) return;
    if (!formData.first_name?.trim()) {
      toast.error('First name is required');
      return;
    }
    if (!formData.last_name?.trim()) {
      toast.error('Last name is required');
      return;
    }
    if (!formData.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!isValidEmail(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (!formData.gender?.trim()) {
      toast.error('Gender is required');
      return;
    }
    const ageNum = formData.age ? parseInt(formData.age, 10) : null;
    if (ageNum == null || ageNum < 1 || ageNum > 120) {
      toast.error('Age is required (1–120)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone?.trim() || null,
        gender: formData.gender.trim(),
        age: ageNum,
        skill_level: formData.skill_level || 'intermediate',
        sports: formData.sports?.length ? formData.sports : [],
        rating: formData.rating ? parseInt(formData.rating, 10) : null,
        team_name: formData.team_name?.trim() || null,
        club: formData.club?.trim() || null,
        division_id: formData.division_id || null
      };
      if (payload.rating !== null && (payload.rating < 0 || payload.rating > 9999)) {
        toast.error('Rating must be between 0 and 9999');
        setSubmitting(false);
        return;
      }
      await axios.post(`${API_URL}/public/tournaments/${tournamentId}/register`, payload);
      setSubmitted(true);
      toast.success("You're registered!");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleSport = (value) => {
    setFormData((prev) => ({
      ...prev,
      sports: prev.sports.includes(value)
        ? prev.sports.filter((s) => s !== value)
        : [...prev.sports, value]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (!info?.tournament || info.error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full bg-card border-border/40">
          <CardContent className="pt-6">
            <p className="text-muted-foreground text-center">
              {info?.error || 'Tournament not found.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { tournament, registration_open, registration_deadline, divisions, sports: sportsList } = info;
  const sportsForSelect = (sportsList && sportsList.length) ? SPORTS.filter((s) => sportsList.includes(s.value)) : SPORTS;

  if (submitted) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Card className="max-w-md w-full bg-card border-border/40">
          <CardContent className="pt-6 text-center space-y-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
            <h2 className="font-heading text-xl font-bold uppercase">You're registered!</h2>
            <p className="text-muted-foreground">
              Thanks for registering for {tournament.name}. The organizer may assign you to divisions or competitions later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!registration_open) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <Card className="max-w-md w-full bg-card border-border/40">
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Trophy className="w-6 h-6 text-primary" />
              {tournament.name}
            </CardTitle>
            {tournament.venue && (
              <CardDescription className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {tournament.venue}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {info.registration_enabled
                ? 'Registration has closed for this tournament.'
                : 'Online registration is not open for this tournament.'}
              {registration_deadline && ' The registration deadline has passed.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6" data-testid="public-registration">
      <div className="max-w-xl mx-auto space-y-6">
        <Card className="bg-card border-border/40">
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <Trophy className="w-6 h-6 text-primary" />
              {tournament.name}
            </CardTitle>
            <CardDescription className="space-y-1">
              {tournament.venue && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> {tournament.venue}
                </span>
              )}
              {tournament.start_date && (
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(tournament.start_date).toLocaleDateString(undefined, { dateStyle: 'long' })}
                  {tournament.end_date && tournament.end_date !== tournament.start_date && (
                    <> – {new Date(tournament.end_date).toLocaleDateString(undefined, { dateStyle: 'long' })}</>
                  )}
                </span>
              )}
              {tournament.description && <p className="mt-2">{tournament.description}</p>}
            </CardDescription>
          </CardHeader>
        </Card>

        <Card className="bg-card border-border/40">
          <CardHeader>
            <CardTitle className="font-heading uppercase flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Register as a player
            </CardTitle>
            <CardDescription>No account needed. Fill in your details to register.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="First name"
                    required
                    className="bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Last name"
                    required
                    className="bg-background/50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  required
                  className="bg-background/50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Phone number"
                  className="bg-background/50"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender *</Label>
                  <Select value={formData.gender || 'none'} onValueChange={(v) => setFormData({ ...formData, gender: v === 'none' ? '' : v })} required>
                    <SelectTrigger id="gender" className="bg-background/50">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Age *</Label>
                  <Input
                    id="age"
                    type="number"
                    min={1}
                    max={120}
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder="Required (1–120)"
                    required
                    className="bg-background/50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="skill_level">Skill level</Label>
                  <Select value={formData.skill_level} onValueChange={(v) => setFormData({ ...formData, skill_level: v })}>
                    <SelectTrigger id="skill_level" className="bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rating">Rating</Label>
                  <Input
                    id="rating"
                    type="number"
                    min={0}
                    max={9999}
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    placeholder="Optional (0–9999)"
                    className="bg-background/50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="team_name">Team name</Label>
                  <Input
                    id="team_name"
                    value={formData.team_name}
                    onChange={(e) => setFormData({ ...formData, team_name: e.target.value })}
                    placeholder="Optional"
                    className="bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="club">Club</Label>
                  <Input
                    id="club"
                    value={formData.club}
                    onChange={(e) => setFormData({ ...formData, club: e.target.value })}
                    placeholder="Optional"
                    className="bg-background/50"
                  />
                </div>
              </div>
              {divisions?.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="division">Division</Label>
                  <select
                    id="division"
                    value={formData.division_id}
                    onChange={(e) => setFormData({ ...formData, division_id: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">No division</option>
                    {divisions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {sportsForSelect.length > 0 && (
                <div className="space-y-2">
                  <Label>Sport(s) you want to play</Label>
                  <div className="flex flex-wrap gap-2">
                    {sportsForSelect.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => toggleSport(s.value)}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm transition-colors",
                          formData.sports.includes(s.value)
                            ? "bg-primary border-primary text-primary-foreground"
                            : "bg-muted/30 border-border/40 hover:border-border"
                        )}
                      >
                        <span>{s.icon}</span>
                        <span>{s.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? 'Registering...' : 'Register'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PublicRegistration;
